type ExpressPlacementBillingBase = {
  /**
   * @deprecated - Use the flatAmount or percentAmount property instead
   * The amount to charge a demand-side partner for Express placement.
   *
   * - For 'flat' feeType, this is the amount in cents per notice
   *   (e.g., 2500 for $25).
   * - For 'percent' feeType, this is the percentage fee as an integer
   *   (e.g., 12 for 12%).
   */
  amount?: number;
  /**
   * When true, we will not charge the demand-side partner an additional
   * convenience fee (based on the publisher configuration) on top of the fee
   * configured here.
   */
  overrideConvenienceFee: boolean;
  /**
   * When true, the full amount owed by the demand-side partner (including
   * publisher amount, automated affidavit fees, and convenience fee) will be
   * charged to the demand-side partner's subscription.
   */
  subscriptionOnlyBilling: boolean;
  /**
   * The Stripe customer to use for billing. This allows us to use a dedicated
   * customer for a demand-side partner's subscription, which is better for our
   * accounting and reporting.
   */
  stripeCustomerId: string;
  /**
   * The Stripe subscription to use for billing placement fees (and possibly all
   * publication costs). If not set, will be created the first time a placement
   * fee is charged.
   */
  stripeSubscriptionId?: string;
  /**
   * When true, the placement-fee subscription pipeline is bypassed entirely:
   * no `PLACEMENT_FEE_BILLING_TRIGGER` event is created on invoice payment,
   * no Stripe subscription is auto-created, and no placement-fee line item
   * is added to a subscription bill.
   *
   * Use this for demand-side partners who pay the notice invoice directly
   * with their own card/ACH aacount — since the placement fee is already embedded
   * as a line item on that notice invoice (via `calculateInvoicePricing`),
   * the notice-invoice payment IS the placement-fee payment. Also having the
   * subscription pipeline fire would double-charge the partner.
   *
   * Mutually exclusive with `subscriptionOnlyBilling` — one says "everything
   * via subscription", the other says "nothing via subscription".
   */
  skipPlacementFeeSubscription?: boolean;
};

export enum ExpressPlacementFeeType {
  FLAT = 'flat',
  PERCENT = 'percent',
  FLAT_PLUS_PERCENT = 'flat_plus_percent'
}

export type ExpressPlacementFlatBilling = ExpressPlacementBillingBase & {
  /**
   * The type of fee to charge a demand-side partner for Express placement.
   *
   * - 'flat' is a uniform per-notice charge added to the demand-side partner's
   *    subscription for each notice they place.
   * - 'percent' is a variable fee calculated as a percentage of the subtotal
   *    (publisher's amount) of the notice.
   * - 'fee_plus_percent' is a fee calculated as a flat amount plus a percentage of the subtotal
   *    (publisher's amount) of the notice.
   */
  feeType: ExpressPlacementFeeType.FLAT;
  /**
   * The flat amount to charge a demand-side partner for Express placement.
   */
  flatAmountInCents: number;
  /**
   * Percent amount is null for flat-amount billing.
   */
  percentAmount: null;
};

export type ExpressPlacementPercentBilling = ExpressPlacementBillingBase & {
  /**
   * The type of fee to charge a demand-side partner for Express placement.
   *
   * - 'flat' is a uniform per-notice charge added to the demand-side partner's
   *    subscription for each notice they place.
   * - 'percent' is a variable fee calculated as a percentage of the subtotal
   *    (publisher's amount) of the notice.
   * - 'fee_plus_percent' is a fee calculated as a flat amount plus a percentage of the subtotal
   *    (publisher's amount) of the notice.
   */
  feeType: ExpressPlacementFeeType.PERCENT;
  /**
   * The percentage fee to charge a demand-side partner for Express placement.
   */
  percentAmount: number;
  /**
   * Flat amount is null for percent-based billing.
   */
  flatAmountInCents: null;
};

export type ExpressPlacementFeePlusPercentBilling =
  ExpressPlacementBillingBase & {
    /**
     * The type of fee to charge a demand-side partner for Express placement.
     *
     * - 'flat' is a uniform per-notice charge added to the demand-side partner's
     *    subscription for each notice they place.
     * - 'percent' is a variable fee calculated as a percentage of the subtotal
     *    (publisher's amount) of the notice.
     * - 'fee_plus_percent' is a fee calculated as a flat amount plus a percentage of the subtotal
     *    (publisher's amount) of the notice.
     */
    feeType: ExpressPlacementFeeType.FLAT_PLUS_PERCENT;
    /**
     * The flat amount to charge a demand-side partner for Express placement.
     */
    flatAmountInCents: number;
    /**
     * The percentage fee to charge a demand-side partner for Express placement, on top of the flatAmount.
     */
    percentAmount: number;
  };

export type ExpressPlacementBilling =
  | ExpressPlacementFlatBilling
  | ExpressPlacementPercentBilling
  | ExpressPlacementFeePlusPercentBilling;
